/**
 * Learn to Play: the tutorial for a first-time player (after 'Have you played Ka'Dice before?' is
 * answered No, see `Welcome.tsx`), and what the lobby's Tutorial tile and Options → Tutorial open.
 *
 * Fifteen steps, one at a time. Each shows a line of text (`learn.<step>`) over a stand-in table
 * that plays the step out with the game's own pieces: card art, deck backs, the dealer chip (here
 * the Starter), image dice with their roll cels, held-die locks, power flames and ▲/▼ arrows,
 * Beginner Mode's kill flames, and the real attack animations (`playAttack`) with their sounds.
 * Timings are shorter than in a game. Next moves on (the last step closes); Replay replays
 * the step; Skip (or Escape) leaves at any time. Client-only: the server knows nothing of it.
 *
 * The cast comes from the card catalog by Kill Quest / power code, so it follows the card data:
 * you are the Knight of 3's (3s wild, killed by a pair of 3s) facing the 3 of a Kind card, the
 * Ka'Dice card and the 4 of a Kind card; a war is fought over the Knight of 5's. Every roll is
 * fixed and checked by hand against those Kill Quests (no accidental kills).
 */
import { useEffect, useLayoutEffect, useRef, useState, type CSSProperties, type ReactNode } from 'react';
import { MAX_SEQUENCES, t, tn, type CardDef, type CardId, type DeckColor } from '@kadice/shared';
import { playSound } from '../audio';
import { playAttack, type PlayingAttack } from '../animations/play';
import { cardImage, killerName, preloadCards, shortPowerOf } from '../cards';
import { HOLDS, ROLLS, SAFE as SAFE_ROLLS, SAVED as SAVED_ROLLS, WAR_ROLL, WAR_TIES } from '../learnRolls';
import { Dice, Pips } from './Dice';
import { fireSrc } from './TurnPanel';
import '../learn.css';

const YOU = 1;
const A = 2;
const B = 3;
const C = 4;
/** The other players, left to right along the top; turns go A, B, C, then you. */
const TOP = [A, B, C] as const;
const ORDER = [A, B, C, YOU] as const;
const COLORS: Record<number, DeckColor> = { [YOU]: 'blue', [A]: 'red', [B]: 'green', [C]: 'purple' };
const DECK = 5;

/** The cards the steps use, found in the catalog by code. */
interface Cast {
  /** Yours: 3s wild, killed by a pair of 3s. */
  knight: CardDef;
  /** Killed by 3 of a kind. */
  three: CardDef;
  /** Killed by a Ka'Dice. */
  kadice: CardDef;
  /** Killed by 4 of a kind. */
  four: CardDef;
  /** Two players flip it in the war steps. */
  war: CardDef;
}

function castFrom(catalog: ReadonlyMap<CardId, CardDef>): Cast | null {
  const cards = [...catalog.values()];
  const find = (ok: (c: CardDef) => boolean) => cards.find(ok);
  const knight = find((c) => c.power === 'w3' && c.killer === 'p3') ?? find((c) => c.power === 'w3');
  const three = find((c) => c.killer === 'xxx');
  const kadice = find((c) => c.killer === 'kadice');
  const four = find((c) => c.killer === 'xxxx');
  const war = find((c) => c.killer === 'p5') ?? find((c) => c !== knight && c !== three && c !== kadice && c !== four);
  return knight && three && kadice && four && war ? { knight, three, kadice, four, war } : null;
}

interface SeatState {
  card: CardId | null;
  up: boolean;
  /** Bumped on each flip, so the card art remounts and plays its turn-over. */
  flip: number;
  /** Cards left in the deck (hand). */
  deck: number;
  /** The last card destroyed, on the discard pile. */
  discard: CardId | null;
  /** Out of cards: the skull. */
  out: boolean;
  /** 0: a place not rolled yet. */
  dice: number[];
  rolling: number[];
  /** Your dice held (gold, 🔒). */
  held: number[];
  /** Another player's dice picked out (gold, lifted): the ones that kill. */
  lifted: number[];
  /** Your dice the power can change: they burn. */
  burning: number[];
  /** An arrow pressed on one of your dice. */
  arrow: { index: number; dir: 'up' | 'down' } | null;
  /** War dice under the card, and how the war roll came out. */
  war: number[] | null;
  warResult: 'won' | 'lost' | 'tie' | null;
}

interface Scene {
  seats: Record<number, SeatState>;
  /** Seats not in the step: faded. */
  dim: number[];
  /** Seats not on the table yet. */
  hidden: number[];
  turn: number | null;
  starter: number | null;
  /** The Starter being drawn: the seat lit for a moment. */
  pick: number | null;
  /** Seats whose vulnerability is pointed out on the card. */
  quest: number[];
  /** Your power pointed out on your card. */
  power: boolean;
  /** Beginner Mode's flames: cards the dice can destroy. */
  fire: number[];
  /** Cards targeted for the attack. */
  target: number[];
  /** Seats at war: a red ring. */
  atWar: number[];
  /** The big word in the middle of the table (key bumps replay its entrance). */
  banner: { text: string; key: number } | null;
  /** Good or bad for you, in the middle of the table. */
  thumb: { up: boolean; text: string } | null;
  /** The button you are about to press on your seat. */
  button: { label: string; pressed: boolean } | null;
  /** Your 'Your Deck' label (step 1). */
  deckLabel: boolean;
  /** 'Turn 2 of 3', pinned over your seat. */
  turnTag: number | null;
  winner: boolean;
}

const seatOf = (card: CardId | null, up = true, deck = DECK): SeatState => ({
  card,
  up,
  flip: 0,
  deck,
  discard: null,
  out: false,
  dice: [],
  rolling: [],
  held: [],
  lifted: [],
  burning: [],
  arrow: null,
  war: null,
  warResult: null,
});

const sceneOf = (seats: Record<number, SeatState>, patch: Partial<Scene> = {}): Scene => ({
  seats,
  dim: [],
  hidden: [],
  turn: null,
  starter: null,
  pick: null,
  quest: [],
  power: false,
  fire: [],
  target: [],
  atWar: [],
  banner: null,
  thumb: null,
  button: null,
  deckLabel: false,
  turnTag: null,
  winner: false,
  ...patch,
});

/** Round 1's table: you the Knight, then the 3 of a Kind, Ka'Dice and 4 of a Kind cards, face up. */
const roundOne = (c: Cast, up = true) => ({
  [YOU]: seatOf(c.knight.id, up),
  [A]: seatOf(c.three.id, up),
  [B]: seatOf(c.kadice.id, up),
  [C]: seatOf(c.four.id, up),
});

/** Rolls that kill nothing on round 1's table, per player, per turn (see `learnRolls.ts`). */
const SAFE: Record<number, readonly (readonly number[])[]> = {
  [A]: SAFE_ROLLS.a,
  [B]: SAFE_ROLLS.b,
  [C]: SAFE_ROLLS.c,
  [YOU]: SAFE_ROLLS.you,
};

/** What each seat rolls and holds on each turn of the order and round steps (see `learnRolls.ts`). */
const SAVED: Record<number, readonly (readonly number[])[]> = {
  [A]: SAVED_ROLLS.a,
  [B]: SAVED_ROLLS.b,
  [C]: SAVED_ROLLS.c,
  [YOU]: SAVED_ROLLS.you,
};
const HELD: Record<number, readonly (readonly number[])[]> = {
  [A]: HOLDS.a,
  [B]: HOLDS.b,
  [C]: HOLDS.c,
  [YOU]: HOLDS.you,
};

type Pace = 'normal' | 'fast';
/** A roll, by pace: ms between dice starting, ms they all spin once started, ms between dice landing. */
const PACE: Record<Pace, { start: number; spin: number; stop: number }> = {
  normal: { start: 220, spin: 550, stop: 160 },
  fast: { start: 60, spin: 260, stop: 60 },
};

/** Plays one step's script against the scene; `stop` ends it (and any attack playing) at once. */
class Runner {
  private alive = true;
  private timers = new Set<number>();
  private attacking: PlayingAttack | null = null;
  private seq = 0;
  private banners = 0;

  constructor(
    private readonly update: (change: (s: Scene) => Scene) => void,
    private readonly table: () => HTMLElement | null,
  ) {}

  stop() {
    this.alive = false;
    for (const id of this.timers) clearTimeout(id);
    this.timers.clear();
    this.attacking?.cancel();
    this.attacking = null;
  }

  /** Resolves after `ms`; never, once stopped (the script just ends there). */
  wait(ms: number): Promise<void> {
    return new Promise((resolve) => {
      const id = window.setTimeout(() => {
        this.timers.delete(id);
        if (this.alive) resolve();
      }, ms);
      this.timers.add(id);
    });
  }

  set(patch: Partial<Scene> | ((s: Scene) => Partial<Scene>)) {
    if (!this.alive) return;
    this.update((s) => ({ ...s, ...(typeof patch === 'function' ? patch(s) : patch) }));
  }

  seat(id: number, patch: Partial<SeatState> | ((s: SeatState) => Partial<SeatState>)) {
    if (!this.alive) return;
    this.update((s) => {
      const was = s.seats[id]!;
      return { ...s, seats: { ...s.seats, [id]: { ...was, ...(typeof patch === 'function' ? patch(was) : patch) } } };
    });
  }

  banner(text: string | null) {
    this.set({ banner: text === null ? null : { text, key: ++this.banners } });
  }

  /** Turn a seat's card face up (a new card from the deck when `card` is given). */
  flip(id: number, card?: CardId) {
    this.seat(id, (s) => ({
      card: card ?? s.card,
      up: true,
      flip: s.flip + 1,
      deck: card !== undefined ? Math.max(0, s.deck - 1) : s.deck,
    }));
  }

  /** Press your seat's button: it shows, then goes in. */
  async press(label: string) {
    this.set({ button: { label, pressed: false } });
    await this.wait(550);
    this.set({ button: { label, pressed: true } });
    await this.wait(250);
    this.set({ button: null });
  }

  /** Roll to `values`, the dice at `keep` staying put; one die after another, as in a game. */
  async roll(id: number, values: readonly number[], { keep = [] as readonly number[], pace = 'normal' as Pace } = {}) {
    const p = PACE[pace];
    const free = values.map((_, i) => i).filter((i) => !keep.includes(i));
    this.seat(id, (s) => ({
      dice: values.map((v, i) => (keep.includes(i) ? (s.dice[i] ?? v) : 0)),
      rolling: [],
      lifted: [],
      held: s.held.filter((i) => keep.includes(i)),
    }));
    for (const [k, i] of free.entries()) {
      if (k > 0) await this.wait(p.start);
      this.seat(id, (s) => ({ dice: s.dice.map((v, j) => (j === i ? values[i]! : v)), rolling: [...s.rolling, i] }));
    }
    await this.wait(p.spin);
    for (const [k, i] of free.entries()) {
      if (k > 0) await this.wait(p.stop);
      this.seat(id, (s) => ({ rolling: s.rolling.filter((j) => j !== i) }));
    }
  }

  /**
   * One turn of the order and round steps: the seat rolls (the dice it held last turn staying put),
   * holds some for the next, and ends its turn with only those left on its seat.
   */
  async turn(id: number, n: number) {
    const kept = n > 0 ? HELD[id]![n - 1]! : [];
    await this.roll(id, SAVED[id]![n]!, { keep: kept, pace: 'fast' });
    await this.wait(250);
    const hold = HELD[id]![n] ?? [];
    for (const i of hold) {
      this.seat(id, (s) => ({ held: [...s.held, i] }));
      await this.wait(130);
    }
    await this.wait(400);
    this.seat(id, (s) => ({ dice: s.dice.map((v, i) => (hold.includes(i) ? v : 0)), held: [...hold] }));
  }

  /** Hold your dice at `indexes`, one tap at a time. */
  async hold(indexes: readonly number[]) {
    for (const i of indexes) {
      this.seat(YOU, (s) => ({ held: [...s.held, i] }));
      await this.wait(260);
    }
  }

  /** Change one of your dice with its arrow, a step at a time, from `from` to `to`. */
  async nudge(index: number, from: number, to: number) {
    const dir = to > from ? 'up' : 'down';
    for (let v = from; v !== to;) {
      this.seat(YOU, { arrow: { index, dir } });
      await this.wait(260);
      v += dir === 'up' ? 1 : -1;
      const now = v;
      this.seat(YOU, (s) => ({ dice: s.dice.map((x, j) => (j === index ? now : x)) }));
      await this.wait(200);
    }
    this.seat(YOU, { arrow: null });
  }

  /** A war roll: `dice` dice under the card. */
  async warRoll(id: number, values: readonly number[]) {
    this.seat(id, { war: values.map(() => 0), warResult: null });
    for (const [i, v] of values.entries()) {
      if (i > 0) await this.wait(120);
      this.seat(id, (s) => ({ war: s.war!.map((x, j) => (j === i ? v : x)) }));
    }
  }

  /**
   * `by` destroys the cards of `victims` with the game's attack animation (and its sounds). Once it
   * is over their cards are on their discard piles: the deck shows, or the skull when it is empty.
   */
  async attack(by: number, victims: number[]) {
    const table = this.table();
    const playing = table ? playAttack(table, { seq: ++this.seq, attackerId: by, victimIds: victims }) : null;
    this.attacking = playing;
    await this.wait(playing ? playing.duration : 600);
    this.set((s) => {
      const seats = { ...s.seats };
      for (const v of victims) {
        const was = seats[v]!;
        seats[v] = {
          ...was,
          discard: was.card,
          card: null,
          up: false,
          out: was.deck === 0,
          dice: [],
          rolling: [],
          lifted: [],
          held: [],
          war: null,
          warResult: null,
        };
      }
      return { seats, fire: [], target: [], quest: s.quest.filter((q) => !victims.includes(q)) };
    });
    // Let the new cards paint before the animation's classes come off, or the old one flashes back.
    await this.wait(60);
    playing?.cancel();
    this.attacking = null;
  }
}

interface Step {
  id: string;
  /** The step's line (`learn.<id>`). */
  text: () => string;
  setup: (c: Cast) => Scene;
  play: (r: Runner, c: Cast, names: Record<number, string>) => Promise<void>;
}

const good = () => ({ up: true, text: t('learn.good') });
const bad = () => ({ up: false, text: t('learn.bad') });

const STEPS: Step[] = [
  {
    id: 'deck',
    text: () => t('learn.deck'),
    setup: () =>
      sceneOf(
        { [YOU]: seatOf(null, false), [A]: seatOf(null, false), [B]: seatOf(null, false), [C]: seatOf(null, false) },
        { hidden: [YOU, A, B, C] },
      ),
    play: async (r) => {
      await r.wait(300);
      r.set({ hidden: [A, B, C], deckLabel: true });
      await r.wait(1300);
      for (const id of TOP) {
        r.set((s) => ({ hidden: s.hidden.filter((h) => h !== id) }));
        await r.wait(350);
      }
    },
  },
  {
    id: 'vuln',
    text: () => t('learn.vuln'),
    setup: (c) => sceneOf(roundOne(c), { dim: [B, C] }),
    play: async (r, c) => {
      // Bad: they roll your vulnerability (a pair of 3s) and destroy your card.
      await r.wait(500);
      r.set({ quest: [YOU] });
      await r.wait(1300);
      r.set({ turn: A });
      await r.roll(A, ROLLS.theyKillYou.dice);
      r.seat(A, { lifted: [...ROLLS.theyKillYou.keep] });
      await r.wait(700);
      await r.attack(A, [YOU]);
      r.set({ thumb: bad(), turn: null });
      await r.wait(1900);
      r.set({ thumb: null });
      r.seat(A, { dice: [], lifted: [] });
      // Good: you flip a new card, roll theirs (3 of a kind) and destroy it.
      await r.wait(300);
      r.flip(YOU, c.knight.id);
      await r.wait(800);
      r.set({ quest: [A], turn: YOU });
      await r.wait(700);
      await r.roll(YOU, ROLLS.youKillThem.dice);
      await r.wait(300);
      r.set({ fire: [A] });
      await r.hold(ROLLS.youKillThem.keep);
      r.set({ target: [A] });
      await r.press(tn('turn.attacking', 1));
      await r.attack(YOU, [A]);
      r.set({ thumb: good(), turn: null });
    },
  },
  {
    id: 'power',
    text: () => t('learn.power'),
    setup: (c) => sceneOf(roundOne(c), { dim: [B, C] }),
    play: async (r) => {
      await r.wait(400);
      r.set({ power: true, quest: [A], turn: YOU });
      await r.wait(900);
      await r.roll(YOU, ROLLS.powerThree.dice);
      await r.wait(300);
      r.seat(YOU, { burning: [1, 2] });
      await r.wait(900);
      for (const [i, from, to] of ROLLS.powerThree.changes) {
        await r.nudge(i, from, to);
        await r.wait(250);
      }
      r.seat(YOU, { burning: [] });
      await r.wait(300);
      r.set({ fire: [A] });
      await r.hold(ROLLS.powerThree.keep);
      r.set({ target: [A] });
      await r.press(tn('turn.attacking', 1));
      await r.attack(YOU, [A]);
      r.set({ thumb: good(), turn: null, power: false });
    },
  },
  {
    id: 'powerKadice',
    text: () => t('learn.powerKadice'),
    setup: (c) => {
      const seats = roundOne(c);
      seats[YOU].dice = [...ROLLS.powerKadice.dice];
      return sceneOf(seats, { dim: [A, C], turn: YOU });
    },
    play: async (r) => {
      await r.wait(400);
      r.set({ power: true, quest: [B] });
      r.seat(YOU, { burning: [1, 2] });
      await r.wait(1200);
      for (const [i, from, to] of ROLLS.powerKadice.changes) {
        await r.nudge(i, from, to);
        await r.wait(250);
      }
      r.seat(YOU, { burning: [] });
      await r.wait(300);
      playSound('kadicePower');
      r.set({ fire: [B] });
      await r.hold(ROLLS.powerKadice.keep);
      r.set({ target: [B] });
      await r.press(tn('turn.attacking', 1));
      await r.attack(YOU, [B]);
      r.set({ thumb: good(), turn: null, power: false });
    },
  },
  {
    id: 'flip',
    text: () => t('learn.flip'),
    setup: (c) => {
      const seats = roundOne(c, false);
      // C has no cards left: nothing to flip.
      seats[C] = { ...seatOf(null, false, 0), discard: c.four.id };
      return sceneOf(seats);
    },
    play: async (r, c) => {
      await r.wait(600);
      for (const id of [A, B, YOU]) {
        r.flip(id, id === YOU ? c.knight.id : id === A ? c.three.id : c.kadice.id);
        await r.wait(500);
      }
      await r.wait(300);
      r.seat(C, { out: true });
    },
  },
  {
    id: 'order',
    text: () => t('learn.order'),
    setup: (c) => sceneOf(roundOne(c)),
    play: async (r, _c, names) => {
      // The Starter is drawn at random.
      await r.wait(400);
      const draw = [YOU, A, B, C, YOU, A, B, C, YOU, A];
      for (const [i, id] of draw.entries()) {
        r.set({ pick: id });
        await r.wait(70 + i * 22);
      }
      r.set({ pick: null, starter: A });
      r.banner(t('learn.starts', { name: names[A]! }));
      await r.wait(1400);
      r.banner(null);
      r.set({ turnTag: 1 });
      for (const id of ORDER) {
        r.set({ turn: id });
        await r.turn(id, 0);
      }
      r.set({ turn: null });
    },
  },
  {
    id: 'round',
    text: () => t('learn.round', { n: MAX_SEQUENCES }),
    setup: (c) => sceneOf(roundOne(c), { starter: A }),
    play: async (r) => {
      for (let turn = 0; turn < MAX_SEQUENCES; turn++) {
        r.set({ turnTag: turn + 1 });
        r.banner(t('learn.turn', { n: turn + 1 }));
        await r.wait(800);
        r.banner(null);
        for (const id of ORDER) {
          r.set({ turn: id });
          await r.turn(id, turn);
        }
      }
      r.set({ turn: null, turnTag: null });
      for (const id of ORDER) r.seat(id, { dice: [], held: [], rolling: [] });
      playSound('roundEnd');
      r.banner(t('learn.roundOver'));
    },
  },
  {
    id: 'war',
    text: () => t('learn.war'),
    setup: (c) =>
      sceneOf(
        {
          [YOU]: seatOf(c.knight.id, false, 4),
          [A]: seatOf(c.war.id, false, 4),
          [B]: seatOf(c.kadice.id, false, 4),
          [C]: seatOf(c.war.id, false, 4),
        },
        { starter: B },
      ),
    play: async (r) => {
      await r.wait(300);
      playSound('roundStart');
      r.banner(t('learn.roundN', { n: 2 }));
      await r.wait(1300);
      r.banner(null);
      await r.wait(300);
      // Only the cards at war turn over until the war is settled.
      r.flip(A);
      await r.wait(450);
      r.flip(C);
      await r.wait(600);
      r.set({ atWar: [A, C], dim: [B, YOU] });
      r.banner(t('learn.warBanner'));
    },
  },
  {
    id: 'warRoll',
    text: () => t('learn.warRoll'),
    setup: (c) =>
      sceneOf(
        {
          [YOU]: seatOf(c.knight.id, false, 4),
          [A]: seatOf(c.war.id, true, 4),
          [B]: seatOf(c.kadice.id, false, 4),
          [C]: seatOf(c.war.id, true, 4),
        },
        { starter: B, atWar: [A, C], dim: [B, YOU] },
      ),
    play: async (r, c, names) => {
      await r.wait(600);
      await r.warRoll(A, WAR_ROLL[0]);
      await r.wait(250);
      await r.warRoll(C, WAR_ROLL[1]);
      await r.wait(500);
      r.seat(A, { warResult: 'won' });
      r.seat(C, { warResult: 'lost' });
      r.banner(t('learn.warWins', { name: names[A]! }));
      await r.wait(1300);
      r.banner(null);
      await r.attack(A, [C]);
      r.set({ atWar: [] });
      // The loser draws a new card.
      await r.wait(500);
      r.flip(C, c.four.id);
    },
  },
  {
    id: 'warTie',
    text: () => t('learn.warTie'),
    setup: (c) =>
      sceneOf(
        {
          [YOU]: seatOf(c.knight.id, false, 4),
          [A]: seatOf(c.war.id, true, 4),
          [B]: seatOf(c.kadice.id, false, 4),
          [C]: seatOf(c.war.id, true, 4),
        },
        { starter: B, atWar: [A, C], dim: [B, YOU] },
      ),
    play: async (r, _c, names) => {
      for (const [i, [a, c]] of WAR_TIES.entries()) {
        await r.wait(500);
        await r.warRoll(A, a);
        await r.wait(200);
        await r.warRoll(C, c);
        await r.wait(500);
        if (i < WAR_TIES.length - 1) {
          r.seat(A, { warResult: 'tie' });
          r.seat(C, { warResult: 'tie' });
          r.banner(t('learn.tie', { n: i + 2 }));
          await r.wait(1500);
          r.banner(null);
          r.seat(A, { war: null, warResult: null });
          r.seat(C, { war: null, warResult: null });
        }
      }
      r.seat(A, { warResult: 'won' });
      r.seat(C, { warResult: 'lost' });
      r.banner(t('learn.warWins', { name: names[A]! }));
      await r.wait(1300);
      r.banner(null);
      await r.attack(A, [C]);
      r.set({ atWar: [] });
    },
  },
  {
    id: 'first',
    text: () => t('learn.first'),
    setup: (c) => sceneOf(roundOne(c), { starter: A }),
    play: async (r) => {
      await r.wait(500);
      r.set({ turn: YOU });
      r.banner(t('learn.yourTurn'));
      await r.wait(1200);
      r.banner(null);
      await r.press(tn('turn.roll', 5));
      await r.roll(YOU, ROLLS.first.dice);
    },
  },
  {
    id: 'kill',
    text: () => t('learn.kill'),
    setup: (c) => sceneOf(roundOne(c), { starter: A, turn: YOU }),
    play: async (r) => {
      // Four 5s: 3 of a kind and 4 of a kind, two cards at once.
      await r.wait(400);
      await r.press(tn('turn.roll', 5));
      await r.roll(YOU, ROLLS.doubleKill.dice);
      await r.wait(300);
      r.set({ fire: [A, C] });
      await r.wait(500);
      await r.hold(ROLLS.doubleKill.keep);
      r.set({ target: [A] });
      await r.wait(400);
      r.set({ target: [A, C] });
      await r.press(tn('turn.attacking', 2));
      await r.attack(YOU, [A, C]);
      playSound('niceJob');
      r.set({ thumb: { up: true, text: t('learn.twoKills') }, turn: null });
    },
  },
  {
    id: 'save',
    text: () => t('learn.save'),
    setup: (c) => sceneOf(roundOne(c), { starter: A, turn: YOU, turnTag: 1 }),
    play: async (r) => {
      await r.wait(400);
      await r.press(tn('turn.roll', 5));
      await r.roll(YOU, ROLLS.saveFirst.dice);
      await r.wait(500);
      await r.hold(ROLLS.saveFirst.keep);
      await r.press(t('turn.end'));
      // Between turns only the held dice stay on your seat.
      r.seat(YOU, (s) => ({ dice: s.dice.map((v, i) => (s.held.includes(i) ? v : 0)) }));
      for (const id of TOP) {
        r.set({ turn: id });
        await r.roll(id, SAFE[id]![0]!, { pace: 'fast' });
        await r.wait(300);
        r.seat(id, { dice: [] });
      }
      r.set({ turn: YOU, turnTag: 2 });
      r.banner(t('learn.turn', { n: 2 }));
      await r.wait(900);
      r.banner(null);
      await r.press(tn('turn.roll', 3));
      await r.roll(YOU, ROLLS.saveSecond.dice, { keep: ROLLS.saveFirst.keep });
      await r.wait(300);
      r.set({ fire: [A] });
      await r.hold(ROLLS.saveSecond.keep.filter((i) => !(ROLLS.saveFirst.keep as readonly number[]).includes(i)));
      r.set({ target: [A] });
      await r.press(tn('turn.attacking', 1));
      await r.attack(YOU, [A]);
      r.set({ thumb: good(), turn: null });
    },
  },
  {
    id: 'greed',
    text: () => t('learn.greed'),
    setup: (c) => sceneOf(roundOne(c), { starter: A, turn: YOU }),
    play: async (r) => {
      // Three 6s could destroy the 3 of a Kind card now; you hold them for the 4 of a Kind as well.
      await r.wait(400);
      await r.press(tn('turn.roll', 5));
      await r.roll(YOU, ROLLS.greedYou.dice);
      await r.wait(300);
      r.set({ fire: [A] });
      await r.wait(700);
      await r.hold(ROLLS.greedYou.keep);
      r.set({ quest: [C] });
      r.banner(t('learn.greedy'));
      await r.wait(1500);
      r.banner(null);
      await r.press(t('turn.end'));
      r.seat(YOU, (s) => ({ dice: s.dice.map((v, i) => (s.held.includes(i) ? v : 0)) }));
      r.set({ fire: [], quest: [], turn: A });
      await r.wait(300);
      await r.roll(A, ROLLS.greedThem.dice);
      r.seat(A, { lifted: [...ROLLS.greedThem.keep] });
      r.set({ quest: [YOU] });
      await r.wait(800);
      await r.attack(A, [YOU]);
      r.set({ thumb: bad(), turn: null, quest: [] });
    },
  },
  {
    id: 'win',
    text: () => t('learn.win'),
    setup: (c) =>
      sceneOf(
        {
          [YOU]: seatOf(c.knight.id, true, 2),
          [A]: seatOf(c.three.id, true, 0),
          [B]: { ...seatOf(null, false, 0), out: true, discard: c.kadice.id },
          [C]: { ...seatOf(null, false, 0), out: true, discard: c.four.id },
        },
        { starter: A, turn: YOU },
      ),
    play: async (r) => {
      await r.wait(400);
      await r.press(tn('turn.roll', 5));
      await r.roll(YOU, ROLLS.win.dice);
      await r.wait(300);
      r.set({ fire: [A] });
      await r.hold(ROLLS.win.keep);
      r.set({ target: [A] });
      await r.press(tn('turn.attacking', 1));
      await r.attack(YOU, [A]);
      r.set({ turn: null, winner: true });
      playSound('niceJob');
      r.banner(t('end.win'));
    },
  },
];

/** One seat: name, deck behind the card (or the skull), the vulnerability and power, dice. */
function SeatView({
  id,
  scene,
  catalog,
  name,
}: {
  id: number;
  scene: Scene;
  catalog: ReadonlyMap<CardId, CardDef>;
  name: string;
}) {
  const s = scene.seats[id]!;
  const you = id === YOU;
  const color = COLORS[id]!;
  const card = s.card !== null && s.up ? catalog.get(s.card) : undefined;
  const classes = ['seat', 'learn-seat', color, s.out ? 'out' : 'active'];
  if (you) classes.push('you', 'learn-big');
  if (scene.turn === id) classes.push('turn');
  if (scene.target.includes(id)) classes.push('selected');
  if (scene.dim.includes(id)) classes.push('learn-dim');
  if (scene.hidden.includes(id)) classes.push('learn-hidden');
  if (scene.pick === id) classes.push('learn-pick');
  if (scene.atWar.includes(id)) classes.push('learn-war');
  if (you && scene.winner) classes.push('winner');

  const art = s.out
    ? '/img/dead.png'
    : card
      ? cardImage(color, card.id)
      : s.deck > 0 || s.card !== null
        ? cardImage(color, 'back')
        : null;
  const backs = Math.min(s.deck, 4);
  const quest = card && scene.quest.includes(id);
  const war = s.war && (
    <div className="learn-war-dice">
      <Dice
        values={s.war}
        winning={s.warResult === 'won' ? s.war.map((_, i) => i) : []}
        losing={s.warResult === 'lost' ? s.war.map((_, i) => i) : []}
      />
      <span className={`learn-war-total${s.warResult === 'tie' ? ' tie' : ''}`}>
        {s.war.every((v) => v > 0) ? t('learn.total', { n: s.war.reduce((a, b) => a + b, 0) }) : ' '}
      </span>
    </div>
  );

  return (
    <div className={classes.join(' ')} data-seat-id={id}>
      {scene.fire.includes(id) && <img className="kill-fire" src={fireSrc(color)} alt="" />}
      <div className="seat-name">
        {you && <span className="you-badge">{t('table.you')}</span>}
        <span>{name}</span>
      </div>
      {you && scene.turnTag !== null && (
        <span key={scene.turnTag} className="learn-turn-tag">
          {t('learn.turnOf', { n: scene.turnTag, total: MAX_SEQUENCES })}
        </span>
      )}
      <div className="learn-body">
        <div className="card-stack">
          {backs > 0 && (
            <span className="deck-stack" aria-hidden>
              {Array.from({ length: backs }, (_, i) => (
                <img
                  key={i}
                  src={cardImage(color, 'back')}
                  alt=""
                  className="card"
                  style={{ '--depth': backs - i } as CSSProperties}
                />
              ))}
              <span className="deck-count" style={{ '--depth': backs } as CSSProperties}>
                {s.deck}
              </span>
            </span>
          )}
          {/* `data-card-of`: what the attack animations move. Its class never changes while one plays. */}
          <div className="card-face" data-card-of={id}>
            {art ? (
              <img
                key={`${s.flip}-${s.up}-${s.out}`}
                src={art}
                alt={card?.name ?? t('card.alt')}
                className={`card${s.out ? ' dead' : ''}${s.flip > 0 && card ? ' learn-flip' : ''}`}
              />
            ) : (
              <div className="card empty" />
            )}
            {quest && <span className="learn-ring quest" aria-hidden />}
            {you && card && scene.power && <span className="learn-ring power" aria-hidden />}
            {scene.starter === id && (
              <>
                <img src="/img/dealer-button.png" alt="" className="dealer-chip" />
                <span className="learn-starter">{t('learn.starter')}</span>
              </>
            )}
          </div>
          {you && scene.deckLabel && <span className="learn-deck-label">{t('learn.yourDeck')}</span>}
        </div>
        {you && (
          <div className="learn-info">
            {card && (
              <span className={`learn-quest${quest ? ' lit' : ''}`}>
                {t('learn.vulnLabel', { quest: killerName(card) })}
              </span>
            )}
            {card && (
              <span className={`learn-power${scene.power ? ' lit' : ''}`}>
                {t('learn.powerLabel', { power: shortPowerOf(card.power) })}
              </span>
            )}
            <span className="learn-action">
              {scene.button && (
                <span className={`learn-btn${scene.button.pressed ? ' pressed' : ''}`}>{scene.button.label}</span>
              )}
            </span>
            {s.discard !== null && <Discard color={color} card={s.discard} />}
          </div>
        )}
      </div>
      {!you && card && (
        <span className={`learn-quest small${quest ? ' lit' : ''}`}>{t(`killquest_shortname.${card.killer}`)}</span>
      )}
      {!you && !card && s.discard !== null && <Discard color={color} card={s.discard} small />}
      {!you && !card && s.discard === null && <span className="learn-quest small blank">{' '}</span>}
      {war ??
        (you ? (
          <YourDice s={s} color={color} />
        ) : (
          // Straight under the seat, where the attack animations look for a victim's dice.
          <Dice values={s.dice.length > 0 ? s.dice : [0, 0, 0, 0, 0]} small rolling={s.rolling} lifted={s.lifted} held={s.held} />
        ))}
    </div>
  );
}

/** A destroyed card on its discard pile, small and grey. */
function Discard({ color, card, small = false }: { color: DeckColor; card: CardId; small?: boolean }) {
  return (
    <span className={`learn-discard${small ? ' small' : ''}`}>
      <img src={cardImage(color, card)} alt="" />
      <span>{t('learn.discard')}</span>
    </span>
  );
}

/** Your dice, as on your turn: big, held ones gold with a 🔒, the ones your power can change burning, an arrow when pressed. */
function YourDice({ s, color }: { s: SeatState; color: DeckColor }) {
  return (
    <div className={`dice-row on-fire learn-dice${s.dice.length === 0 ? ' none' : ''}`}>
      <div className="dice big dice-line">
        {Array.from({ length: 5 }, (_, i) => {
          const v = s.dice[i] ?? 0;
          if (v === 0) {
            return (
              <div key={i} className="die-slot empty">
                <span className="die ghost" />
              </div>
            );
          }
          const rolling = s.rolling.includes(i);
          const held = s.held.includes(i);
          const arrow = s.arrow?.index === i ? s.arrow.dir : null;
          return (
            <div key={i} className={`die-slot${held ? ' held' : ''}`}>
              {arrow && <span className={`learn-arrow ${arrow}`}>{arrow === 'up' ? '▲' : '▼'}</span>}
              <div className="die-fx">
                {s.burning.includes(i) && !rolling && <img className="fire" src={fireSrc(color)} alt="" />}
                <span className={`die${held ? ' held' : ''}${rolling ? ' rolling' : ''}`}>
                  <Pips value={v} rolling={rolling} />
                </span>
                {held && (
                  <span className="die-lock" aria-hidden>
                    🔒
                  </span>
                )}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

function Confetti() {
  return (
    <div className="learn-confetti" aria-hidden>
      {Array.from({ length: 36 }, (_, i) => (
        <span
          key={i}
          style={
            {
              '--x': `${(i * 37) % 100}%`,
              '--d': `${(i % 9) * 0.12}s`,
              '--r': `${(i * 53) % 360}deg`,
              '--c': ['var(--accent)', 'var(--blue)', 'var(--green)', 'var(--red)', 'var(--purple)'][i % 5],
            } as CSSProperties
          }
        />
      ))}
    </div>
  );
}

export function LearnToPlay({
  catalog,
  myName,
  onClose,
}: {
  catalog: ReadonlyMap<CardId, CardDef>;
  myName: string;
  onClose: () => void;
}) {
  const cast = castFrom(catalog);
  const [index, setIndex] = useState(0);
  // Bumped by Replay: the step starts over.
  const [take, setTake] = useState(0);
  const table = useRef<HTMLDivElement>(null);
  const textBox = useRef<HTMLDivElement>(null);
  const step = STEPS[index]!;
  const last = index === STEPS.length - 1;
  // Each play of a step (and the catalog arriving) is one run; its scene starts from the step's setup.
  const run = `${index}:${take}:${cast ? cast.knight.id : ''}`;
  const [played, setPlayed] = useState<{ run: string; scene: Scene } | null>(null);
  const [doneRun, setDoneRun] = useState<string | null>(null);
  const scene = cast ? (played?.run === run ? played.scene : step.setup(cast)) : null;
  const done = doneRun === run;

  const names: Record<number, string> = {
    [YOU]: myName || t('learn.you'),
    [A]: t('tutorial.player', { n: 2 }),
    [B]: t('tutorial.player', { n: 3 }),
    [C]: t('tutorial.player', { n: 4 }),
  };

  useEffect(() => {
    if (!cast) return;
    const colors = Object.values(COLORS);
    preloadCards(
      [...catalog.keys()]
        .flatMap((id) => colors.map((c) => cardImage(c, id)))
        .concat(colors.map((c) => cardImage(c, 'back'))),
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [!!cast]);

  // Play the step's script on a fresh table.
  useEffect(() => {
    if (!cast) return;
    const runner = new Runner(
      (change) => setPlayed((p) => ({ run, scene: change(p?.run === run ? p.scene : step.setup(cast)) })),
      () => table.current,
    );
    let live = true;
    void step.play(runner, cast, names).then(() => live && setDoneRun(run));
    return () => {
      live = false;
      runner.stop();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [run]);

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => e.key === 'Escape' && onClose();
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose]);

  // Size the live text to fill its box: the biggest font that still fits.
  useLayoutEffect(() => {
    const box = textBox.current;
    const live = box?.querySelector<HTMLElement>('.learn-text.live');
    if (!box || !live) return;
    const fit = () => {
      let lo = 12;
      let hi = 64;
      while (hi - lo > 0.5) {
        const mid = (lo + hi) / 2;
        live.style.fontSize = `${mid}px`;
        if (live.scrollHeight <= box.clientHeight - 8 && live.scrollWidth <= box.clientWidth) lo = mid;
        else hi = mid;
      }
      live.style.fontSize = `${lo}px`;
    };
    fit();
    const ro = new ResizeObserver(fit);
    ro.observe(box);
    return () => ro.disconnect();
  }, [index, !!cast]);

  const next = () => (last ? onClose() : setIndex(index + 1));
  const back = () => index > 0 && setIndex(index - 1);

  let body: ReactNode;
  if (!cast || !scene) {
    body = <p className="learn-loading">{t('app.loading')}</p>;
  } else {
    body = (
      <div ref={table} className="table learn-table">
        <div className="learn-top">
          {TOP.map((id) => (
            <SeatView key={id} id={id} scene={scene} catalog={catalog} name={names[id]!} />
          ))}
        </div>
        <div className="learn-bottom">
          <SeatView id={YOU} scene={scene} catalog={catalog} name={names[YOU]!} />
        </div>
        {scene.banner && (
          <div key={scene.banner.key} className="learn-banner" aria-live="polite">
            {scene.banner.text}
          </div>
        )}
        {scene.thumb && (
          <div className={`learn-thumb ${scene.thumb.up ? 'up' : 'down'}`} aria-live="polite">
            <span className="learn-thumb-icon" aria-hidden>
              {scene.thumb.up ? '👍' : '👎'}
            </span>
            <strong>{scene.thumb.text}</strong>
          </div>
        )}
        {scene.winner && <Confetti />}
      </div>
    );
  }

  return (
    <div className="learn" role="dialog" aria-modal="true" aria-label={t('learn.title')}>
      <header className="learn-head">
        <span className="learn-progress">
          {t('learn.title')} · {t('learn.progress', { n: index + 1, total: STEPS.length })}
        </span>
        <button className="learn-skip" onClick={onClose}>
          {t('learn.skip')}
        </button>
      </header>
      {/* A fixed box above the table (the attack animations pass under it); the live text is sized to fill it. */}
      <div className="learn-texts" ref={textBox}>
        {STEPS.map((s) => (
          <div key={s.id} className={`learn-text${s === step ? ' live' : ''}`} aria-hidden={s !== step}>
            <p>{s.text()}</p>
          </div>
        ))}
      </div>
      {body}
      <footer className="learn-foot">
        <button onClick={back} disabled={index === 0}>
          {t('learn.back')}
        </button>
        <button onClick={() => setTake((n) => n + 1)} disabled={!done}>
          {t('learn.again')}
        </button>
        <button className="primary" onClick={next}>
          {t(last ? 'learn.done' : 'common.next')}
        </button>
      </footer>
    </div>
  );
}
